/**
 * This store contains the features available in the model.
 * Features can be enabled or disabled by the user inside the model,
 * but also by team permissions.
 * This store is used to check if a feature is enabled or not.
 */

import { computed } from "vue";
import { reactiveComputed } from "@vueuse/core";
import { defineStore } from "pinia";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useStakeholderStore } from "@editor/items/stakeholder/stakeholders.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useIndicatorStore } from "@editor/items/indicator/indicators.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useFlowStore } from "@editor/items/flow/flows.store";
import { useSceneStore } from "@editor/items/scene/scenes.store";

export type ModelFeatures = {
  target: boolean | undefined;
  resource: boolean | undefined;
  index: boolean | undefined;
  stakeholder: boolean | undefined;
  momentum: boolean | undefined;
  template: boolean | undefined;

  impact: boolean | undefined;
  flow: boolean | undefined;

  incident: boolean | undefined;
  action: boolean | undefined;
  scheduledEffect: boolean | undefined;

  indicator: boolean | undefined;

  scene: boolean | undefined;
  design: boolean | undefined;

  // Beta features
  netChart: boolean | undefined;
};
type FeatureKey = keyof ModelFeatures;

export const defaultFeatures: ModelFeatures = {
  target: true,
  resource: true,
  index: true,
  
  stakeholder: true,
  momentum: true,
  template: true,

  impact: true,
  flow: true,

  incident: true,
  action: true,
  scheduledEffect: true,

  indicator: true,

  design: true,
  
  netChart: true,

  scene: undefined
};

const betaFeatures = {
  scene: true
};
type BetaFeatureKey = keyof typeof betaFeatures;

type StandardFeatureKey = Exclude<FeatureKey, BetaFeatureKey>;

export const useFeatureStore = defineStore("model/features", {
  state: (): ModelFeatures => {
    const features = { ...defaultFeatures };

    if (process.env.NODE_ENV === "development") {
      Object.assign(features, betaFeatures);
    }

    return features;
  },
  getters: {
    enabled(): (featureName: keyof ModelFeatures) => boolean {
      return (featureName) => {
        return this.featuresInUse[featureName] || this[featureName] || false;
      };
    },
    featuresInUse(): ModelFeatures {
      const targetStore = useTargetStore();
      const resourceStore = useResourceStore();
      const indexStore = useIndexStore();

      return {
        target: targetStore.orderedItems.length > 0,
        resource: resourceStore.orderedItems.length > 0,
        index: indexStore.orderedItems.length > 0,
        stakeholder: useStakeholderStore().orderedItems.length > 0,
        indicator: useIndicatorStore().orderedItems.length > 0,
        momentum: targetStore.orderedItems.some(
          (target) => target.momentumActive == true
        ),
        template: useSceneStore().orderedItems.some((scene) => scene.templateId != null),
        impact: useRelationStore().orderedItems.length > 0,
        flow: useFlowStore().orderedItems.length > 0,
        incident: useIncidentStore().orderedItems.length > 0,
        action: useActionStore().orderedItems.length > 0,
        scheduledEffect: useSimulationStore().orderedItems.some((sim) =>
          Object.values(sim.actionPlans).some(
            (ap) => ap.scheduledEffects.length > 0
          )
        ),
        design: false,
        netChart: false,
        scene: useSceneStore().orderedItems.length > 0
      };
    },
    features(state): ModelFeatures {
      const featureKeys = Object.keys(
        defaultFeatures
      ) as FeatureKey[];
      return featureKeys.reduce((features, featureName) => {
        features[featureName] =
          this.featuresInUse[featureName] || state[featureName] || false;
        return features;
      }, {} as ModelFeatures);
    },
    standardFeatures(state): Pick<ModelFeatures, StandardFeatureKey> {
      const featureKeys = Object.keys(
        defaultFeatures
      ) as FeatureKey[];
      return featureKeys.reduce((features, featureName: FeatureKey) => {
        if (!(featureName in betaFeatures)) {
          features[featureName as StandardFeatureKey] =
            this.featuresInUse[featureName] || state[featureName] || false;
        }
        return features;
      }, {} as Pick<ModelFeatures, StandardFeatureKey>);
    },
    betaFeatures(state): Pick<ModelFeatures, BetaFeatureKey> {
      return Object.keys(betaFeatures).reduce((features, featureName) => {
        features[featureName as BetaFeatureKey] =
          state[featureName as BetaFeatureKey] || false;
        return features;
      }, {} as Pick<ModelFeatures, BetaFeatureKey>);
    },
    betaFeaturesActive(): boolean {
      const result = Object.keys(betaFeatures).some((featureName) => {
        return this[featureName as keyof ModelFeatures] === true;
      });
      return result;
    }
  },
  actions: {
    set(featureName: keyof ModelFeatures, value: boolean) {
      // Beta features can't be set to false, only to undefined.
      // That way, they won't be disabled when the next update
      // is released and they move from beta to stable.
      if (featureName in betaFeatures) {
        if (value) {
          this[featureName] = true;
        } else {
          this[featureName] = undefined;
        }
      } else {
        this[featureName] = value;
      }
    },

    activateBetaFeatures() {
      Object.keys(betaFeatures).forEach((featureName) => {
        this[featureName as keyof ModelFeatures] = true;
      });
    },
    deactivateBetaFeatures() {
      Object.keys(betaFeatures).forEach((featureName) => {
        this[featureName as keyof ModelFeatures] = false;
      });
    }
  }
});

export function useFeatures() {
  const featureStore = useFeatureStore();
  return reactiveComputed(() => featureStore.features);
}

export function useFeature(featureName: keyof ModelFeatures) {
  const featureStore = useFeatureStore();
  return computed(() => featureStore.features[featureName]);
}
